import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Plus, Building2, LogOut, Users, Activity } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", admin_email: "", admin_password: "", admin_name: "", plan: "standard", note: "",
  });

  const load = () => {
    api.get("/admin/tenants").then((r) => setTenants(r.data));
    api.get("/admin/stats").then((r) => setStats(r.data));
  };
  useEffect(load, []);

  const save = async () => {
    if (!form.name || !form.admin_email || !form.admin_password || !form.admin_name) {
      toast.error("必須項目を入力してください"); return;
    }
    try {
      await api.post("/admin/tenants", form);
      toast.success("テナントを作成しました");
      setOpen(false);
      setForm({ name: "", admin_email: "", admin_password: "", admin_name: "", plan: "standard", note: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "作成失敗");
    }
  };

  const toggleStatus = async (t) => {
    const newStatus = t.status === "active" ? "suspended" : "active";
    await api.patch(`/admin/tenants/${t.id}`, { status: newStatus });
    toast.success(`ステータスを「${newStatus === "active" ? "有効" : "停止"}」に変更しました`);
    load();
  };

  return (
    <div className="min-h-screen bg-[#FBFBF9]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5E3]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#2C5545] flex items-center justify-center text-white">
              <Building2 size={20} />
            </div>
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-[#8A8A86] font-numeric">SUPER ADMIN</div>
              <div className="font-bold text-[#1C1C1A]">運営管理コンソール</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium">{user?.name}</div>
              <div className="text-xs text-[#8A8A86]">運営者</div>
            </div>
            <Button data-testid="super-logout" variant="outline" size="sm" onClick={() => { logout(); nav("/login"); }}>
              <LogOut size={14} className="mr-1" />ログアウト
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-8" data-testid="super-admin-page">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              ["契約数", stats.tenants, Building2],
              ["ユーザー", stats.users, Users],
              ["利用者", stats.patients, Users],
              ["訪問実績", stats.visits, Activity],
              ["看護記録", stats.records, Activity],
            ].map(([l, v, Icon]) => (
              <Card key={l} className="p-5 border-[#E5E5E3] bg-white">
                <div className="flex items-start justify-between mb-3">
                  <div className="text-xs tracking-[0.1em] uppercase text-[#8A8A86]">{l}</div>
                  <Icon size={16} className="text-[#2C5545]" />
                </div>
                <div className="text-3xl font-bold font-numeric text-[#1C1C1A]">{v}</div>
              </Card>
            ))}
          </div>
        )}

        {/* Tenants */}
        <div>
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold">契約テナント</h2>
              <p className="text-sm text-[#4A4A48]">{tenants.length}社</p>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-tenant-button" className="bg-[#2C5545] text-white"><Plus size={16} className="mr-2" />新規テナント</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>新規テナント作成</DialogTitle></DialogHeader>
                <div className="space-y-3 py-3">
                  <div><Label>ステーション名<span className="text-[#A74141]">*</span></Label><Input data-testid="tenant-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div><Label>プラン</Label>
                    <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="flex h-10 w-full rounded-md border border-input px-3 text-sm">
                      <option value="standard">Standard</option>
                      <option value="trial">Trial</option>
                    </select>
                  </div>
                  <div className="border-t pt-3 text-sm text-[#4A4A48]">初期管理者</div>
                  <div><Label>管理者氏名<span className="text-[#A74141]">*</span></Label><Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} /></div>
                  <div><Label>管理者メール<span className="text-[#A74141]">*</span></Label><Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} /></div>
                  <div><Label>初期パスワード<span className="text-[#A74141]">*</span></Label><Input value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} /></div>
                  <div><Label>備考</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>キャンセル</Button>
                  <Button data-testid="save-tenant-button" className="bg-[#2C5545] text-white" onClick={save}>作成</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="border-[#E5E5E3] bg-white">
            <div className="divide-y divide-[#E5E5E3]">
              {tenants.length === 0 ? (
                <div className="py-16 text-center text-[#8A8A86]">テナントがありません</div>
              ) : (
                tenants.map((t) => (
                  <div key={t.id} className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#D9E2D5] flex items-center justify-center">
                      <Building2 size={18} className="text-[#2C5545]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{t.name}</div>
                      <div className="text-xs text-[#8A8A86] font-numeric">
                        {t.plan} · ユーザー{t.user_count || 0}名 · 利用者{t.patient_count || 0}名 · {t.created_at?.slice(0, 10)}
                      </div>
                    </div>
                    <Badge
                      className={t.status === "active"
                        ? "bg-[#D9E2D5] text-[#2C5545]"
                        : "bg-[#F3F3F1] text-[#8A8A86]"}
                    >
                      {t.status === "active" ? "有効" : t.status === "suspended" ? "停止" : t.status}
                    </Badge>
                    <Button data-testid={`toggle-tenant-${t.id}`} variant="outline" size="sm" onClick={() => toggleStatus(t)}>
                      {t.status === "active" ? "停止" : "再開"}
                    </Button>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
